import React, { useEffect, useState } from "react";
import { Row, Col, Input, InputNumber, Select, Button } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { apiGetAllUnits } from "services/shopUnitService";

// The site (display, cart, shipping calc) reads one combined string, e.g.
// "20g" — these two helpers are purely a data-entry convenience so the admin
// edits a number and a unit separately instead of typing that string by hand.
const parseWeight = (weight) => {
  const str = String(weight ?? "").trim();
  const m = str.match(/^([\d]+(?:[.,]\d+)?)\s*([a-zA-Z]+)?$/);
  if (!m) return { value: "", unit: "g" }; // legacy/unparseable — leave editable, don't guess
  return { value: m[1], unit: m[2] || "g" };
};

const formatWeight = (value, unit) => (value === "" || value === null || value === undefined ? "" : `${value}${unit}`);

// A blank variant row. `id` is only present for variants that already exist in the DB.
const blankVariant = () => ({
  name: "",
  weight: "",
  sku: "",
  price: 0,
  stock_qty: 0,
  status: "1",
});

// Fallback shown only if the units API is unreachable, so the editor never
// renders an empty/broken unit dropdown.
const FALLBACK_UNIT_OPTIONS = [{ value: "g", label: "g" }];

function VariantsEditor(props) {
  const { variants, setVariants, errorField, setErrorField } = props;
  const list = Array.isArray(variants) ? variants : [];
  const [unitOptions, setUnitOptions] = useState(FALLBACK_UNIT_OPTIONS);

  useEffect(() => {
    apiGetAllUnits()
      .then((units) => {
        const options = (units || []).map((u) => ({ value: u.name, label: u.name }));
        if (options.length > 0) setUnitOptions(options);
      })
      .catch(() => {
        // keep the fallback list — this is a data-entry convenience, not critical path
      });
  }, []);

  const updateRow = (index, field, value) => {
    const next = list.map((v, i) => (i === index ? { ...v, [field]: value } : v));
    setVariants(next);
    if (setErrorField) setErrorField(errorField.filter((x) => x !== "variants"));
  };

  const updateWeight = (index, part, value) => {
    const current = parseWeight(list[index].weight);
    const next = part === "value" ? { ...current, value } : { ...current, unit: value };
    updateRow(index, "weight", formatWeight(next.value, next.unit));
  };

  const addRow = () => {
    setVariants([...list, blankVariant()]);
  };

  const removeRow = (index) => {
    setVariants(list.filter((_, i) => i !== index));
  };

  return (
    <div style={{ marginTop: 6 }}>
      <div className="shop-section-head">
        <h4 className="shop-section-title">
          Variants&nbsp;<span style={{ color: "var(--greyColor)", fontWeight: 400 }}>(weights / sizes)</span>
          <span className="req">*</span>
        </h4>
        <Button className="shop-add-btn" icon={<PlusOutlined />} onClick={addRow}>
          Add variant
        </Button>
      </div>

      {errorField && errorField.includes("variants") && (
        <div className="shop-variant-error">
          Add at least one variant with a weight/size.
        </div>
      )}

      {list.length === 0 && (
        <div className="shop-variant-empty">
          No variants yet. Click <b>Add variant</b> to add a weight/size (e.g. 250 g) with its price and stock.
        </div>
      )}

      {list.length > 0 && (
        <Row gutter={10} className="shop-variant-colhead">
          <Col span={4}>Weight</Col>
          <Col span={4}>Unit</Col>
          <Col span={5}>SKU / Code</Col>
          <Col span={5}>Price</Col>
          <Col span={6}>Stock</Col>
        </Row>
      )}

      {list.map((v, index) => {
        const { value: weightValue, unit: weightUnit } = parseWeight(v.weight);
        return (
          <div className="shop-variant-row" key={index}>
            <Row gutter={10} align="middle">
              <Col span={4}>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={1}
                  placeholder="e.g. 250"
                  value={weightValue === "" ? null : Number(weightValue)}
                  onChange={(val) => updateWeight(index, "value", val === null ? "" : val)}
                />
              </Col>
              <Col span={4}>
                <Select
                  style={{ width: "100%" }}
                  value={weightUnit}
                  onChange={(val) => updateWeight(index, "unit", val)}
                  options={unitOptions}
                />
              </Col>
              <Col span={5}>
                <Input
                  placeholder="SKU"
                  value={v.sku || ""}
                  onChange={(e) => updateRow(index, "sku", e.target.value)}
                />
              </Col>
              <Col span={5}>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={0.01}
                  placeholder="0.00"
                  value={v.price}
                  onChange={(value) => updateRow(index, "price", value === null ? 0 : value)}
                />
              </Col>
              <Col span={6}>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  step={1}
                  placeholder="0"
                  value={v.stock_qty}
                  onChange={(value) => updateRow(index, "stock_qty", value === null ? 0 : value)}
                />
              </Col>
            </Row>
            <Button
              type="text"
              className="shop-variant-del"
              icon={<DeleteOutlined />}
              onClick={() => removeRow(index)}
            />
          </div>
        );
      })}
      {list.length > 0 && (
        <div style={{ marginTop: 6, fontSize: 12, color: "var(--darkGreyColor)" }}>
          Weight and unit are shown to customers together (e.g. "250g") and also drive
          international shipping (Japan, etc.) for g/kg. Non-weight units like ml or pcs
          display fine but can't be used for shipping — those orders will show "quotation
          required" for zones that need it.
        </div>
      )}
    </div>
  );
}

export default VariantsEditor;
